import { useMemo, useState } from 'react';
import { AlertTriangle, BookOpen, ChevronDown, FileWarning, Search, ServerCrash } from 'lucide-react';
import { Badge, Button, Checkbox, Input, Skeleton, cn } from '@respectly/ui';
import type { ChatKnowledgeDoc } from '@respectly/shared-types';

/**
 * The widget's knowledge source — the reason this page exists.
 *
 * A widget answers **only** from the documents ticked here. An empty selection is
 * a valid configuration and means the assistant answers from nothing and hands
 * off; it deliberately does not fall back to the whole corpus, because a
 * misconfigured widget that answers plausibly is much harder to notice than one
 * that says it cannot help. That is why an empty selection gets a loud, explicit
 * panel rather than a quiet zero.
 *
 * Each row shows the document's summary, its `##` section headings (which are the
 * units the retriever actually chunks on) and its size, so the choice is informed
 * rather than a list of slugs.
 */
export function KnowledgePicker({
  selected,
  onChange,
  docs,
  configured,
  loading,
  missing,
  ownDocumentCount,
  documentsKnown,
}: {
  selected: string[];
  onChange: (next: string[]) => void;
  docs: ChatKnowledgeDoc[];
  configured: boolean;
  loading: boolean;
  /** Selected slugs that are no longer in the corpus. */
  missing: string[];
  /**
   * Active documents uploaded for this widget. Ticking nothing here only means
   * "answers from nothing" when there are none of those either — a widget on
   * someone else's site is expected to have an empty selection.
   */
  ownDocumentCount: number;
  /**
   * Whether `ownDocumentCount` is an answer or just an absence of one.
   *
   * The uploaded documents arrive in a second request, so for the first moments
   * of every page load the count is 0 because nothing has been fetched — not
   * because nothing exists. Announcing "this widget will answer from nothing"
   * from that is how a widget carrying 23 uploaded documents got told it had
   * none. Until this is true, the picker states the tick count and nothing more.
   */
  documentsKnown: boolean;
}) {
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return docs;
    return docs.filter(
      (d) =>
        d.title.toLowerCase().includes(q) ||
        d.slug.includes(q) ||
        d.summary.toLowerCase().includes(q) ||
        d.sections.some((s) => s.toLowerCase().includes(q))
    );
  }, [docs, query]);

  const toggle = (slug: string) => {
    onChange(selected.includes(slug) ? selected.filter((s) => s !== slug) : [...selected, slug]);
  };

  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-16 rounded-xl" />
        ))}
      </div>
    );
  }

  // Unreachable is NOT the same as empty. An empty picker reads as "there are no
  // documents", and an admin who believes that will save a widget that answers
  // from nothing without meaning to.
  if (!configured) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/50 dark:bg-amber-950/30">
        <div className="flex gap-3">
          <ServerCrash className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
              The knowledge service is not reachable
            </p>
            <p className="text-sm text-amber-800 dark:text-amber-300/90">
              The document list comes from the ops-tools container. This widget&rsquo;s existing
              selection is unchanged and still in effect — you just can&rsquo;t edit it until the
              service responds. Check that <code className="rounded bg-amber-100 px-1 font-mono text-xs dark:bg-amber-900/50">OPS_MCP_BASE_URL</code>{' '}
              and <code className="rounded bg-amber-100 px-1 font-mono text-xs dark:bg-amber-900/50">OPS_MCP_AUTH_TOKEN</code>{' '}
              are set on the API.
            </p>
            {selected.length > 0 && (
              <p className="pt-1 font-mono text-xs text-amber-700 dark:text-amber-400">
                Currently selected: {selected.join(', ')}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Selection state, stated plainly at the top. */}
      {selected.length === 0 && !documentsKnown ? (
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Nothing from Respectly&rsquo;s documentation. Checking this widget&rsquo;s own
          documents&hellip;
        </p>
      ) : selected.length === 0 && ownDocumentCount === 0 ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-900/50 dark:bg-red-950/30">
          <div className="flex gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600 dark:text-red-400" />
            <div>
              <p className="text-sm font-medium text-red-900 dark:text-red-200">
                This widget will answer from nothing
              </p>
              <p className="mt-1 text-sm text-red-800 dark:text-red-300/90">
                Nothing is ticked here and no documents have been uploaded below, so the assistant
                cannot answer a single product question. It will say so and offer to bring in a
                person. That is a legitimate setup for a site whose documentation hasn&rsquo;t been
                written yet — but it is never accidental.
              </p>
            </div>
          </div>
        </div>
      ) : selected.length === 0 ? (
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Nothing from Respectly&rsquo;s documentation. This widget answers from its own{' '}
          {ownDocumentCount} document{ownDocumentCount === 1 ? '' : 's'} below — which is the right
          setup for a widget on a site that isn&rsquo;t ours.
        </p>
      ) : (
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            <span className="font-semibold text-slate-900 dark:text-slate-100">
              {selected.length}
            </span>{' '}
            of {docs.length} documents. The assistant answers only from these.
          </p>
          <div className="flex gap-1.5">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onChange(docs.map((d) => d.slug))}
              disabled={selected.length === docs.length}
            >
              Select all
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => onChange([])}>
              Clear
            </Button>
          </div>
        </div>
      )}

      {missing.length > 0 && (
        <div className="flex gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 dark:border-amber-900/50 dark:bg-amber-950/30">
          <FileWarning className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
          <p className="text-xs text-amber-800 dark:text-amber-300">
            {missing.length} selected document{missing.length === 1 ? '' : 's'} no longer{' '}
            {missing.length === 1 ? 'exists' : 'exist'} in the corpus (
            <span className="font-mono">{missing.join(', ')}</span>). They are ignored at run time,
            so the widget still works — it just knows less than the list suggests. Untick them to
            tidy up.
          </p>
        </div>
      )}

      {docs.length > 6 && (
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by title, topic or section heading"
            className="pl-9"
          />
        </div>
      )}

      <div className="space-y-1.5">
        {filtered.map((doc) => {
          const isSelected = selected.includes(doc.slug);
          const isOpen = expanded === doc.slug;
          return (
            <div
              key={doc.slug}
              className={cn(
                'rounded-xl border transition-colors',
                isSelected
                  ? 'border-sky-300 bg-sky-50/60 dark:border-sky-800 dark:bg-sky-950/20'
                  : 'border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900'
              )}
            >
              <div className="flex items-start gap-3 p-3">
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => toggle(doc.slug)}
                  aria-label={`Include ${doc.title}`}
                  className="mt-0.5"
                />
                <button
                  type="button"
                  onClick={() => toggle(doc.slug)}
                  className="min-w-0 flex-1 text-left"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                      {doc.title}
                    </span>
                    <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                      {doc.slug}
                    </code>
                  </div>
                  <p className="mt-1 line-clamp-2 text-xs text-slate-500 dark:text-slate-400">
                    {doc.summary}
                  </p>
                </button>
                <div className="flex shrink-0 items-center gap-2">
                  <Badge variant="secondary" className="text-[11px]">
                    {doc.sections.length} section{doc.sections.length === 1 ? '' : 's'}
                  </Badge>
                  <span className="hidden text-[11px] tabular-nums text-slate-400 sm:inline">
                    {formatSize(doc.chars)}
                  </span>
                  <button
                    type="button"
                    aria-label={`${isOpen ? 'Hide' : 'Show'} sections in ${doc.title}`}
                    aria-expanded={isOpen}
                    onClick={() => setExpanded(isOpen ? null : doc.slug)}
                    className="rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                  >
                    <ChevronDown
                      className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-180')}
                    />
                  </button>
                </div>
              </div>

              {isOpen && (
                <div className="border-t border-slate-100 px-3 py-2.5 pl-11 dark:border-slate-800">
                  <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-slate-400">
                    <BookOpen className="h-3 w-3" />
                    Sections the assistant can retrieve
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {doc.sections.map((section) => (
                      <span
                        key={section}
                        className="rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                      >
                        {section}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {filtered.length === 0 && docs.length > 0 && (
          <p className="py-8 text-center text-sm text-slate-400">
            No document matches &ldquo;{query}&rdquo;.
          </p>
        )}
        {docs.length === 0 && (
          <p className="py-8 text-center text-sm text-slate-400">
            The public corpus is empty. Documents live in{' '}
            <code className="rounded bg-slate-100 px-1 font-mono text-xs dark:bg-slate-800">
              services/agent-fleet/knowledge-public/
            </code>
            .
          </p>
        )}
      </div>
    </div>
  );
}

function formatSize(chars: number): string {
  if (chars < 1000) return `${chars} chars`;
  return `${(chars / 1000).toFixed(1)}k chars`;
}
